/**
 * WifiAccessPointsList — Puntos de acceso WiFi (estándar de listados del CMDB).
 *
 * Antes: tabla propia (`wol-table`), modal de borrado propio, emojis para el
 * tipo de montaje, hex a mano y la ficha abriendo **directamente en edición**.
 *
 * Ahora: `ListPage` con buscador, filtros, columnas, vistas y export; la ficha
 * entra en **lectura** (con botón Editar) y el borrado usa el diálogo único del
 * CMDB.
 *
 * Novedad: columna **Cableado**. El switch y el puerto donde está pinchado un AP
 * no están en la tabla: los resuelve el backend recorriendo la cadena
 * AP → toma de pared → patch panel → switch. Se piden con `?uplink=1` (una
 * consulta más al cargar) y se pintan como `switch · puerto`.
 *
 * Botones propios:
 *  · **Ver el switch** — a la ficha del switch al que está conectado.
 *  · **Ver en el plano** — al plano de planta de su ubicación.
 *  · **Ver su controlador** — abre el controlador (con `?destacar&abrir=1`).
 *  · **Copiar** — usa el endpoint de clonado del AP (`POST /wifi/aps/<id>/clone`),
 *    que copia la configuración y deja en blanco serie, asset, MAC e IP.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  SignalIcon,
  ListBulletIcon,
  Squares2X2Icon,
  MapIcon,
  ServerStackIcon,
  Square3Stack3DIcon,
} from '@heroicons/react/24/outline';
import ListPage from './common/ListPage';
import BulkPlacementAction from './common/BulkPlacementAction';
import BulkEditAction from './common/BulkEditAction';
import CloneDialog from './common/CloneDialog';
import WifiAccessPointForm from './WifiAccessPointForm';
import { useListView } from '../views/ViewsContext';
import { docElementPath } from '../utils/docElements';
import { getServerFormStyle } from '../utils/serverFormStyle';
import useLocationPaths from '../hooks/useLocationPaths';
import useNavOrder from '../hooks/useNavOrder';
import { matchesLocation } from './LocationFilter';
import api from '../services/api';

const STATUS_META = {
  active:      { label: 'Activo',        badge: 'list-badge--on' },
  maintenance: { label: 'Mantenimiento', badge: 'list-badge--warn' },
  planned:     { label: 'Planificado',   badge: 'list-badge' },
  decom:       { label: 'Decom',         badge: 'list-badge--off' },
};

const MOUNT_LABEL = {
  ceiling: 'Techo',
  wall: 'Pared',
  desktop: 'Sobremesa',
  outdoor: 'Exterior',
  pole: 'Poste',
  other: 'Otro',
};

export default function WifiAccessPointsList({ companyId }) {
  const { t } = useTranslation();
  const tr = useCallback((key, fallback) => {
    const value = t(key);
    return !value || value === key ? fallback : value;
  }, [t]);
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const initController = searchParams.get('controllerId') || '';

  const [aps, setAps] = useState([]);
  const estiloFicha = getServerFormStyle();
  const { locations: todasUbicaciones, pathFor } = useLocationPaths(companyId);
  const [controllers, setControllers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [bulkResetKey, setBulkResetKey] = useState(0);

  const [search, setSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterCtrl, setFilterCtrl] = useState(initController);
  const [filterBand, setFilterBand] = useState('');
  const [filterStandard, setFilterStandard] = useState('');
  const [filterLoc, setFilterLoc] = useState([]);
  const [filterPoe, setFilterPoe] = useState('');
  const [hlId, setHlId] = useState(null);

  const [selected, setSelected] = useState(null);   // null = lista
  const [initialMode, setInitialMode] = useState('edit');

  /* copia (pregunta el nombre con el diálogo estándar) */
  const [cloneSource, setCloneSource] = useState(null);
  const [cloning, setCloning] = useState(false);
  const [cloneError, setCloneError] = useState('');

  const load = useCallback(async () => {
    if (!companyId) return;
    setLoading(true);
    setError('');
    try {
      // `uplink: 1` trae también el switch y el puerto de cada AP.
      const [apRes, ctrlRes] = await Promise.all([
        api.getWifiAPs(companyId, { uplink: 1 }),
        api.getWifiControllers(companyId).catch(() => ({ data: [] })),
      ]);
      setAps(apRes.data || []);
      setControllers(ctrlRes.data || []);
    } catch (e) {
      setError(e.message || 'Error cargando los puntos de acceso');
    } finally {
      setLoading(false);
    }
  }, [companyId]);

  useEffect(() => { load(); }, [load]);

  /* ?destacar=<id>[&abrir=1] y limpieza de ?controllerId */
  useEffect(() => {
    const raw = searchParams.get('destacar');
    if (!raw || !aps.length) return undefined;
    const id = Number(raw);
    const target = aps.find(a => a.id === id);
    if (!target) return undefined;
    const abrir = searchParams.get('abrir') === '1';
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.delete('destacar');
      next.delete('abrir');
      return next;
    }, { replace: true });
    if (abrir) { setInitialMode('view'); setSelected(target); return undefined; }
    setHlId(id);
    const row = document.getElementById(`wifi-ap-row-${id}`);
    if (row) row.scrollIntoView({ behavior: 'smooth', block: 'center' });
    const timer = setTimeout(() => setHlId(null), 4000);
    return () => clearTimeout(timer);
  }, [searchParams, aps, setSearchParams]);

  /* ── ficha ──────────────────────────────────────────────────────────────── */
  const openView = (ap) => { setInitialMode('view'); setSelected(ap); };
  const openEdit = (ap) => { setInitialMode('edit'); setSelected(ap); };
  const openCreate = () => { setInitialMode('create'); setSelected({}); };
  const closeForm = () => { setSelected(null); load(); };

  const handleDelete = async (ap) => {
    try {
      await api.deleteWifiAP(ap.id, companyId);
      await load();
    } catch (e) {
      setError(e.message || 'Error eliminando el punto de acceso');
    }
  };

  /* ── copiar (el backend clona la configuración y blanquea la identidad) ─── */
  const handleClone = async (newName) => {
    if (!cloneSource) return;
    setCloning(true);
    setCloneError('');
    try {
      await api.cloneWifiAP(cloneSource.id, newName, companyId);
      setCloneSource(null);
      await load();
    } catch (e) {
      setCloneError(e.message || 'Error clonando el punto de acceso');
    } finally {
      setCloning(false);
    }
  };

  /* ── opciones de los filtros ────────────────────────────────────────────── */
  const bandOptions = useMemo(
    () => [...new Set(aps.map(a => a.band).filter(Boolean))],
    [aps],
  );
  const standardOptions = useMemo(
    () => [...new Set(aps.map(a => a.wifi_standard).filter(Boolean))],
    [aps],
  );

  /* Ubicaciones que realmente usan los APs: el filtro no enseña el árbol
     entero de la empresa si solo hay APs en tres sitios. */
  const usedLocationIds = useMemo(
    () => [...new Set(aps.map(a => a.location_id).filter(Boolean))],
    [aps],
  );

  /* ── filtrado ───────────────────────────────────────────────────────────── */
  const filtered = useMemo(() => {
    const needle = search.trim().toLowerCase();
    return aps.filter((a) => {
      if (filterStatus && (a.status || 'active') !== filterStatus) return false;
      if (filterCtrl === 'none' && a.controller_id) return false;
      if (filterCtrl && filterCtrl !== 'none' && String(a.controller_id) !== filterCtrl) return false;
      if (filterBand && a.band !== filterBand) return false;
      if (filterStandard && a.wifi_standard !== filterStandard) return false;
      if (!matchesLocation(todasUbicaciones, filterLoc, a.location_id)) return false;
      if (filterPoe === 'yes' && !a.poe_powered) return false;
      if (filterPoe === 'no' && a.poe_powered) return false;
      if (!needle) return true;
      return (a.name || '').toLowerCase().includes(needle)
        || (a.code || '').toLowerCase().includes(needle)
        || (a.mac_address || '').toLowerCase().includes(needle)
        || (a.management_ip || '').toLowerCase().includes(needle)
        || (a.model || '').toLowerCase().includes(needle)
        || (a.serial_number || '').toLowerCase().includes(needle)
        || (a.vendor_label || a.vendor || '').toLowerCase().includes(needle)
        || (a.location_name || '').toLowerCase().includes(needle)
        || (pathFor(a.location_id) || '').toLowerCase().includes(needle)
        || (a.location_room || '').toLowerCase().includes(needle)
        || (a.controller_name || '').toLowerCase().includes(needle);
    });
  }, [aps, search, filterStatus, filterCtrl, filterBand, filterStandard, filterLoc, filterPoe, pathFor, todasUbicaciones]);

  // Orden de las fichas congelado mientras la ficha está abierta.
  const navAps = useNavOrder(filtered, selected, selected !== null);

  /** Texto del cableado: «switch · puerto». */
  const uplinkLabel = (ap) => {
    const u = ap.uplink;
    if (!u) return null;
    const sw = u.switch?.name || null;
    const port = u.switch_port?.label || u.switch_port?.name || u.port_label || null;
    if (!sw && !port) return null;
    return [sw, port].filter(Boolean).join(' · ');
  };

  /* ── columnas ───────────────────────────────────────────────────────────── */
  const columns = useMemo(() => {
    const dash = <span className="list-hint">—</span>;
    return [
      {
        key: 'name',
        label: tr('wifi.name', 'Nombre'),
        sortable: true,
        render: (a) => (
          <span style={{ display: 'inline-flex', flexDirection: 'column' }}>
            <span style={{ fontWeight: 600 }}>{a.name}</span>
            {(a.code || a.mac_address) && (
              <span className="list-hint list-code">{[a.code, a.mac_address].filter(Boolean).join(' · ')}</span>
            )}
          </span>
        ),
      },
      {
        key: 'vendor',
        label: tr('wifi.vendor_model', 'Fabricante / modelo'),
        sortable: true,
        sortValue: (a) => `${a.vendor_label || a.vendor || ''} ${a.model || ''}`.trim(),
        render: (a) => (
          <span style={{ display: 'inline-flex', flexDirection: 'column' }}>
            <span>{a.vendor_label || a.vendor || dash}</span>
            <span className="list-hint">{a.model || ''}</span>
          </span>
        ),
      },
      {
        key: 'wifi_standard',
        label: tr('wifi.standard_band', 'Estándar / banda'),
        sortable: true,
        sortValue: (a) => `${a.wifi_standard || ''} ${a.band || ''}`.trim(),
        render: (a) => (
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
            <span className="list-badge">{a.wifi_standard_label || a.wifi_standard || dash}</span>
            {a.band_label && <span className="list-badge list-badge--warn">{a.band_label}</span>}
            {a.poe_powered && <span className="list-badge list-badge--on" title={tr('wifi.poe_hint', 'Alimentado por PoE')}>PoE</span>}
          </span>
        ),
      },
      {
        key: 'connection',
        label: tr('wifi.connection', 'Cableado'),
        sortable: true,
        sortValue: (a) => uplinkLabel(a) || '',
        render: (a) => {
          const label = uplinkLabel(a);
          if (!label) {
            return <span className="list-hint" title={tr('wifi.no_uplink', 'Sin cableado registrado')}>{dash}</span>;
          }
          const switchId = a.uplink?.switch?.id;
          return (
            <button
              type="button"
              className="list-code"
              style={{ cursor: switchId ? 'pointer' : 'default', background: 'none', border: 0, padding: 0 }}
              title={tr('wifi.view_switch', 'Ver el switch al que está conectado')}
              onClick={(event) => {
                event.stopPropagation();
                const path = switchId ? docElementPath('switch', switchId) : null;
                if (path) navigate(path);
              }}
            >
              {label}
            </button>
          );
        },
      },
      {
        key: 'location',
        label: tr('list.location', 'Ubicación'),
        sortable: true,
        sortValue: (a) => pathFor(a.location_id) || a.location_name || '',
        render: (a) => {
          if (!a.location_id && !a.location_name && !a.location_room) return dash;
          // Ruta completa (Sede › Planta › Sala): con muchas sedes, el nombre
          // suelto no distingue una «Sala 1» de otra.
          const ruta = pathFor(a.location_id) || a.location_name || '';
          const extra = [a.location_floor, a.location_room].filter(Boolean).join(' · ');
          return (
            <span title={[ruta, extra].filter(Boolean).join(' · ')} style={{ display: 'inline-flex', flexDirection: 'column' }}>
              <span>{ruta || dash}</span>
              {extra && <span className="list-hint">{extra}</span>}
            </span>
          );
        },
      },
      {
        key: 'mount_type',
        label: tr('wifi.mount', 'Montaje'),
        sortable: true,
        sortValue: (a) => a.mount_type || '',
        render: (a) => (a.mount_type
          ? <span className="list-badge">{a.mount_type_label || MOUNT_LABEL[a.mount_type] || a.mount_type}</span>
          : dash),
      },
      {
        key: 'controller_name',
        label: tr('wifi.controller', 'Controlador'),
        sortable: true,
        sortValue: (a) => a.controller_name || '',
        render: (a) => (a.controller_name
          ? (
            <button
              type="button"
              className="list-badge"
              style={{ cursor: 'pointer' }}
              title={tr('wifi.view_controller', 'Ver su controlador')}
              onClick={(event) => {
                event.stopPropagation();
                navigate(`/wifi/controllers?destacar=${a.controller_id}&abrir=1`);
              }}
            >
              {a.controller_name}
            </button>
          )
          : <span className="list-badge list-badge--warn">{tr('wifi.standalone', 'Standalone')}</span>),
      },
      {
        key: 'management_ip',
        label: tr('wifi.mgmt_ip', 'IP de gestión'),
        sortable: true,
        render: (a) => (a.management_ip ? <span className="list-code">{a.management_ip}</span> : dash),
      },
      {
        key: 'status',
        label: tr('ipam.status', 'Estado'),
        sortable: true,
        sortValue: (a) => a.status || 'active',
        render: (a) => {
          const meta = STATUS_META[a.status] || STATUS_META.active;
          return <span className={`list-badge ${meta.badge}`}>{meta.label}</span>;
        },
      },
      {
        key: 'notes',
        label: tr('description', 'Notas'),
        render: (a) => a.notes || dash,
      },
    ];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [t, tr, navigate, pathFor]);

  const defaultVisible = ['name', 'vendor', 'wifi_standard', 'connection', 'location', 'controller_name', 'management_ip', 'status'];

  const filters = [
    {
      key: 'status', label: tr('ipam.status', 'Estado'), allLabel: tr('ipam.status_all', 'Todos los estados'),
      value: filterStatus, onChange: setFilterStatus,
      options: Object.entries(STATUS_META).map(([value, meta]) => ({ value, label: meta.label })),
    },
    {
      key: 'controller', label: tr('wifi.controller', 'Controlador'), allLabel: tr('wifi.controller_all', 'Todos los controladores'),
      value: filterCtrl, onChange: setFilterCtrl,
      options: [
        ...controllers.map(c => ({ value: String(c.id), label: c.name })),
        { value: 'none', label: tr('wifi.no_controller', 'Sin controlador (standalone)') },
      ],
    },
    {
      key: 'band', label: tr('wifi.band', 'Banda'), allLabel: tr('wifi.band_all', 'Todas las bandas'),
      value: filterBand, onChange: setFilterBand,
      options: bandOptions.map(b => ({ value: b, label: b })),
    },
    {
      key: 'standard', label: tr('wifi.standard', 'Estándar'), allLabel: tr('wifi.standard_all', 'Todos los estándares'),
      value: filterStandard, onChange: setFilterStandard,
      options: standardOptions.map(s => ({ value: s, label: s.toUpperCase() })),
    },
    {
      key: 'location', type: 'locations', label: tr('list.location', 'Ubicación'),
      companyId,
      value: filterLoc, onChange: setFilterLoc,
      // Solo las ubicaciones usadas por algún AP (con su rama, para la ruta).
      onlyIds: usedLocationIds,
    },
    {
      key: 'poe', label: 'PoE', allLabel: tr('wifi.poe_all', 'Con y sin PoE'),
      value: filterPoe, onChange: setFilterPoe,
      options: [
        { value: 'yes', label: tr('wifi.poe_yes', 'Con PoE') },
        { value: 'no', label: tr('wifi.poe_no', 'Sin PoE') },
      ],
    },
  ];

  const subtitle = `${aps.length} ${tr('wifi.ap_plural', 'puntos de acceso')}`
    + ` · ${filtered.length} ${tr('location.filtered', 'filtrados')}`;

  const exportRows = useMemo(() => filtered.map(a => ({
    'Código': a.code || '',
    'Nombre': a.name || '',
    'Fabricante': a.vendor_label || a.vendor || '',
    'Modelo': a.model || '',
    'Serie': a.serial_number || '',
    'MAC': a.mac_address || '',
    'IP de gestión': a.management_ip || '',
    'Estándar': a.wifi_standard_label || a.wifi_standard || '',
    'Banda': a.band_label || a.band || '',
    'PoE': a.poe_powered ? 'Sí' : 'No',
    'Montaje': a.mount_type_label || MOUNT_LABEL[a.mount_type] || a.mount_type || '',
    'Ubicación': pathFor(a.location_id) || a.location_name || '',
    'Planta': a.location_floor || '',
    'Sala': a.location_room || '',
    'Controlador': a.controller_name || 'Standalone',
    'Cableado': uplinkLabel(a) || '',
    'Estado': (STATUS_META[a.status] || STATUS_META.active).label,
  })), [filtered, pathFor]);

  /** Tarjetas: lo esencial del AP, con los mismos botones de fila. */
  function renderCards({ rows, rowActions }) {
    return (
      <div className="list-cards">
        {rows.map((a) => {
          const meta = STATUS_META[a.status] || STATUS_META.active;
          const label = uplinkLabel(a);
          return (
            <div
              key={a.id}
              id={`wifi-ap-card-${a.id}`}
              className={`list-card${a.id === hlId ? ' list-row-highlight' : ''}`}
              role="button"
              tabIndex={0}
              onClick={() => openView(a)}
              onKeyDown={(event) => { if (event.key === 'Enter') openView(a); }}
              title={`${tr('action.view', 'Ver')} ${a.name}`}
            >
              <div className="list-card-head">
                <div>
                  <div className="list-card-title">{a.name}</div>
                  {(a.code || a.mac_address) && (
                    <span className="list-code">{[a.code, a.mac_address].filter(Boolean).join(' · ')}</span>
                  )}
                </div>
                <span className={`list-badge ${meta.badge}`}>{meta.label}</span>
              </div>

              <div className="list-card-meta">
                <span>{a.vendor_label || a.vendor || '—'}{a.model ? ` · ${a.model}` : ''}</span>
                {a.wifi_standard_label && <span className="list-badge">{a.wifi_standard_label}</span>}
                {a.band_label && <span className="list-badge list-badge--warn">{a.band_label}</span>}
                {a.poe_powered && <span className="list-badge list-badge--on">PoE</span>}
                {a.mount_type && <span className="list-badge">{a.mount_type_label || MOUNT_LABEL[a.mount_type] || a.mount_type}</span>}
              </div>

              <div className="list-card-meta">
                {(a.location_id || a.location_name || a.location_room) && (
                  <span>📍 {[pathFor(a.location_id) || a.location_name, a.location_room].filter(Boolean).join(' · ')}</span>
                )}
                {a.controller_name
                  ? <span>{tr('wifi.controller', 'Controlador')}: {a.controller_name}</span>
                  : <span className="list-hint">{tr('wifi.standalone', 'Standalone')}</span>}
                {a.management_ip && <span className="list-code">{a.management_ip}</span>}
                {label && <span className="list-code">{label}</span>}
              </div>

              {a.notes && <div className="list-card-meta"><span>{a.notes}</span></div>}

              <div className="list-card-actions">{rowActions(a)}</div>
            </div>
          );
        })}
      </div>
    );
  }

  const AP_VIEWS = useMemo(() => [
    { key: 'list', label: tr('ipam.view_list', 'Lista'), icon: ListBulletIcon, title: tr('ipam.view_list_hint', 'Tabla con todas las columnas') },
    { key: 'card', label: tr('ipam.view_cards', 'Fichas'), icon: Squares2X2Icon, title: tr('ipam.view_cards_hint', 'Tarjetas con lo esencial de cada AP'), render: renderCards },
    // eslint-disable-next-line react-hooks/exhaustive-deps
  ], [t, hlId, pathFor]);
  useListView('wifi_aps', 'list');

  if (!companyId) {
    return (
      <div className="list-page" style={{ padding: '1.5rem' }}>
        <div className="alert alert-warning">
          {tr('wifi.select_company', 'Selecciona una empresa para ver los puntos de acceso.')}
        </div>
      </div>
    );
  }

  return (
    <>
      {selected !== null && (
        <WifiAccessPointForm
          ap={selected && selected.id ? selected : null}
          companyId={companyId}
          initialMode={initialMode}
          variant={estiloFicha === 'cards' ? 'cards' : 'classic'}
          navItems={navAps}
          navIndex={selected && selected.id ? navAps.findIndex(a => String(a.id) === String(selected.id)) : -1}
          onNavigate={(destino) => { setSelected(destino); }}
          onClose={closeForm}
          onSave={(creado) => {
            load();
            // Alta: el listado adopta el registro creado y la ficha sigue abierta.
            if (creado?.id) { setInitialMode('edit'); setSelected(creado); }
          }}
        />
      )}

      <div style={{ display: selected !== null ? 'none' : undefined }}>
        <ListPage
          icon={SignalIcon}
          title={tr('wifi.aps_title', 'Puntos de acceso')}
          subtitle={subtitle}

          onCreate={openCreate}
          createLabel={tr('wifi.new_ap', 'Nuevo punto de acceso')}
          createTitle={tr('wifi.new_ap_hint', 'AP suelto o gestionado por un controlador')}
          createDisabled={!companyId}

          search={search}
          onSearchChange={setSearch}
          searchPlaceholder={tr('wifi.ap_search', 'Buscar por nombre, MAC, IP, ubicación…')}
          filters={filters}

          exportRows={exportRows}
          exportFileBase="wifi-aps"
          exportSheetName="Puntos de acceso"

          columns={columns}
          columnStorageKey="cmdb_cols_wifi_aps"
          companyId={companyId}
          defaultVisible={defaultVisible}
          rows={filtered}
          rowKey={(a) => a.id}
          rowId={(a) => `wifi-ap-row-${a.id}`}
          rowClassName={(a) => (a.id === hlId ? 'list-row-highlight' : '')}
          onRowClick={openView}
          rowTitle={(a) => `${tr('action.view', 'Ver')} ${a.name}`}

          selectable
          selectionResetKey={bulkResetKey}
          bulkActions={({ selectedRows }) => (
            <>
              <BulkPlacementAction
                companyId={companyId}
                entity="wifi_aps"
                supportsRack={false}
                selectedRows={selectedRows}
                onApplied={() => {
                  setBulkResetKey((key) => key + 1);
                  load();
                }}
              />
              <BulkEditAction
                companyId={companyId}
                entity="wifi_aps"
                selectedRows={selectedRows}
                onApplied={() => {
                  setBulkResetKey((key) => key + 1);
                  load();
                }}
              />
            </>
          )}

          views={AP_VIEWS}
          listKey="wifi_aps"
          defaultView="list"

          loading={loading}
          loadingText={tr('msg.loading', 'Cargando puntos de acceso…')}
          error={error}

          empty={{
            icon: '📶',
            title: aps.length === 0
              ? tr('wifi.ap_empty', 'No hay puntos de acceso')
              : tr('msg.no_results', 'Ningún AP coincide con los filtros'),
            subtitle: aps.length === 0
              ? tr('wifi.ap_empty_hint', 'Da de alta el AP y, si lo gestiona un controlador, asígnalo.')
              : undefined,
            action: aps.length === 0 ? (
              <button type="button" className="list-btn-primary" onClick={openCreate}>
                ＋ {tr('wifi.new_ap', 'Nuevo punto de acceso')}
              </button>
            ) : undefined,
          }}

          actions={{
            extra: (a) => {
              const switchId = a.uplink?.switch?.id;
              const switchPath = switchId ? docElementPath('switch', switchId) : null;
              return (
                <>
                  {/* Ver el switch al que está cableado */}
                  <button
                    type="button"
                    className="list-action-btn"
                    disabled={!switchPath}
                    onClick={() => switchPath && navigate(switchPath)}
                    title={uplinkLabel(a)
                      ? `${tr('wifi.view_switch', 'Ver el switch al que está conectado')}: ${uplinkLabel(a)}`
                      : tr('wifi.no_uplink', 'Sin cableado registrado')}
                  >
                    <ServerStackIcon />
                  </button>
                  {/* Ver en el plano de su planta */}
                  <button
                    type="button"
                    className="list-action-btn"
                    disabled={!a.location_id}
                    onClick={() => navigate('/wifi/map')}
                    title={a.location_name
                      ? `${tr('wifi.view_on_map', 'Ver en el plano')}: ${a.location_name}`
                      : tr('wifi.no_location', 'Este AP no tiene ubicación asignada')}
                  >
                    <MapIcon />
                  </button>
                  {/* Ver su controlador */}
                  <button
                    type="button"
                    className="list-action-btn"
                    disabled={!a.controller_id}
                    onClick={() => navigate(`/wifi/controllers?destacar=${a.controller_id}&abrir=1`)}
                    title={a.controller_name
                      ? `${tr('wifi.view_controller', 'Ver su controlador')}: ${a.controller_name}`
                      : tr('wifi.standalone', 'Standalone')}
                  >
                    <Square3Stack3DIcon />
                  </button>
                </>
              );
            },
            onCopy: (a) => { setCloneError(''); setCloneSource(a); },
            onEdit: openEdit,
            onDelete: handleDelete,
            deleteTitle: tr('wifi.ap_delete_title', 'Eliminar punto de acceso'),
            deleteMessage: (a) => (
              <>
                {tr('wifi.ap_delete_confirm', '¿Seguro que quieres eliminar')} <strong>{a.name}</strong>
                {a.mac_address ? ` (${a.mac_address})` : ''}?{' '}
                {tr('wifi.ap_delete_hint', 'Se eliminarán también sus SSIDs, sus radios y su cableado. No se puede deshacer.')}
              </>
            ),
          }}
        />
      </div>

      {/* ── Copiar AP: pide el nombre y explica qué se copia ───────────── */}
      {cloneSource && (
        <CloneDialog
          title={tr('wifi.clone_title', 'Copiar punto de acceso')}
          nameLabel={tr('wifi.clone_name', 'Nombre del nuevo AP')}
          defaultName={`${cloneSource.name} (copia)`}
          busy={cloning}
          error={cloneError}
          onConfirm={handleClone}
          onClose={() => { setCloneSource(null); setCloneError(''); }}
        />
      )}
    </>
  );
}
